import React, { useState, useEffect, useRef } from "react";
import { predictStage, compressImage, getVerificationTickets, acknowledgeVerification, raiseVerification } from "../api";
import VerifiedReportsModal from "./VerifiedReportsModal";

const getImageUrl = (img) => {
  if (!img) return "";
  if (
    img.startsWith("data:") ||
    img.startsWith("http://") ||
    img.startsWith("https://") ||
    img.startsWith("/") ||
    img.startsWith("static/") ||
    img.startsWith("blob:")
  ) {
    return img;
  }
  return `data:image/jpeg;base64,${img}`;
};

export default function StageDetection({ onBack, onToggleSidebar, isSidebarOpen, onLogout }) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);
  const [uiState, setUiState] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const anotherInputRef = useRef(null);
  const anotherFolderInputRef = useRef(null);
  const thumbnailStripRef = useRef(null);

  // Folder Processing states
  const [processedResults, setProcessedResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [folderLoadingImage, setFolderLoadingImage] = useState(null);

  useEffect(() => {
    if (thumbnailStripRef.current && processedResults.length > 0) {
      const container = thumbnailStripRef.current;
      const item = container.children[activeIndex];
      if (item) {
        const containerWidth = container.clientWidth;
        const itemLeft = item.offsetLeft;
        const itemWidth = item.offsetWidth;
        container.scrollTo({ left: itemLeft - containerWidth / 2 + itemWidth / 2, behavior: 'smooth' });
      }
    }
  }, [activeIndex, processedResults.length]);

  const [isDragOver, setIsDragOver] = useState(false);

  // Single Result states
  const [singleResult, setSingleResult] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const [isPanActive, setIsPanActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);

  const [history, setHistory] = useState([]);

  const stagesTimeline = ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"];

  useEffect(() => {
    const saved = localStorage.getItem("stage_history_v3");
    if (saved) {
      setHistory(JSON.parse(saved).slice(0, 3));
    } else {
      const defaultHistory = [
        {
          id: "act-1",
          filename: "Maize.jpg",
          date: "Aug 06, 2026",
          size: "2.1 MB",
          thumbnail: "/assets/recent/maize-healthy.jpg",
          prediction: { label: "Flowering", confidence: 0.97, activeStageIndex: 2 }
        },
        {
          id: "act-2",
          filename: "corn_edge_field_7.png",
          date: "Aug 01, 2026",
          size: "1.2 MB",
          thumbnail: "/assets/recent/maize-leaf-spot.jpg",
          prediction: { label: "Vegetative", confidence: 0.91, activeStageIndex: 1 }
        },
        {
          id: "act-3",
          filename: "soybean-bacterial-blight.jpg",
          date: "Jul 28, 2026",
          size: "1.7 MB",
          thumbnail: "/assets/recent/soybean-bacterial-blight.jpg",
          prediction: { label: "Maturity", confidence: 0.86, activeStageIndex: 4 }
        }
      ];
      setHistory(defaultHistory);
      localStorage.setItem("stage_history_v3", JSON.stringify(defaultHistory));
    }
  }, []);

  // Restore prediction session from sessionStorage on mount (for page refreshes)
  useEffect(() => {
    const savedSession = sessionStorage.getItem("stage_prediction_session");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.uiState === 2 && session.singleResult) {
          setSingleResult(session.singleResult);
          setUiState(2);
        } else if (session.uiState === 3 && session.processedResults && session.processedResults.length > 0) {
          setProcessedResults(session.processedResults);
          setActiveIndex(session.activeIndex || 0);
          setUiState(3);
        }
      } catch (e) {
        console.warn("Failed to parse saved stage_prediction_session", e);
      }
    }
  }, []);

  const persistSingleSession = async (resultEntry) => {
    try {
      let compressedImg = resultEntry.image;
      try {
        compressedImg = await compressImage(resultEntry.image, 600, 600, 0.7);
      } catch (e) {
        console.warn("Failed to compress single result image for sessionStorage", e);
      }
      sessionStorage.setItem("stage_prediction_session", JSON.stringify({
        uiState: 2,
        singleResult: {
          ...resultEntry,
          image: compressedImg
        }
      }));
    } catch (e) {
      console.warn("Failed to save stage single session to sessionStorage", e);
    }
  };

  const persistFolderSession = async (results, idx = 0) => {
    try {
      const compressedResults = await Promise.all(results.map(async (res) => {
        try {
          const comp = await compressImage(res.image, 400, 400, 0.6);
          return { ...res, image: comp };
        } catch (_) {
          return res;
        }
      }));
      sessionStorage.setItem("stage_prediction_session", JSON.stringify({
        uiState: 3,
        processedResults: compressedResults,
        activeIndex: idx
      }));
    } catch (e) {
      console.warn("Failed to save stage folder session to sessionStorage", e);
    }
  };

  const [verificationSent, setVerificationSent] = useState(false);
  const [flaggedImages, setFlaggedImages] = useState({});

  useEffect(() => {
    if (singleResult?.isFlagged) {
      setVerificationSent(true);
    } else {
      setVerificationSent(false);
    }
  }, [singleResult]);

  const [userTickets, setUserTickets] = useState([]);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  const loadUserTickets = React.useCallback(async () => {
    const sessionId = localStorage.getItem("cropscan_session_id");
    if (!sessionId) return;
    try {
      const res = await getVerificationTickets(sessionId);
      if (res.status === "success") setUserTickets(res.tickets || []);
    } catch (err) {
      console.warn("Failed to load user tickets", err);
    }
  }, []);

  useEffect(() => {
    loadUserTickets();
  }, [loadUserTickets]);

  const handleFlagVerification = async () => {
    if (!singleResult) return;
    setLoading(true);
    try {
      let imgData = singleResult.image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...singleResult.prediction, source_module: "stage" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setVerificationSent(true);
      await loadUserTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };

  const handleFlagFolderImage = async () => {
    if (!processedResults[activeIndex]) return;
    setLoading(true);
    try {
      let imgData = processedResults[activeIndex].image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...processedResults[activeIndex].prediction, source_module: "stage" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setFlaggedImages(prev => ({ ...prev, [activeIndex]: true }));
      await loadUserTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    try {
      sessionStorage.removeItem("stage_prediction_session");
    } catch (e) { }
  };

  const saveToHistory = async (filename, prediction, imgData) => {
    let thumb = imgData;
    try {
      thumb = await compressImage(imgData);
    } catch (e) {
      console.warn("Image compression failed, using original base64", e);
    }

    const newEntry = {
      id: `act-${Date.now()}`,
      filename,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      size: "1.3 MB",
      thumbnail: thumb,
      prediction
    };
    const updated = [newEntry, ...history.slice(0, 2)];
    setHistory(updated);
    try {
      localStorage.setItem("stage_history_v3", JSON.stringify(updated));
    } catch (e) {
      console.warn("LocalStorage write failed, resetting key", e);
      localStorage.removeItem("stage_history");
      try {
        localStorage.setItem("stage_history_v3", JSON.stringify([newEntry]));
      } catch (retryErr) {
        console.error("Critical localStorage write failure", retryErr);
      }
    }
  };

  const saveFolderToHistory = async (folderResults) => {
    if (!folderResults || folderResults.length === 0) return;
    const lastThree = folderResults.slice(-3).reverse();
    const newEntries = await Promise.all(
      lastThree.map(async (item) => {
        let thumb = item.image;
        try {
          thumb = await compressImage(item.image);
        } catch (e) {
          console.warn("Thumbnail compression failed", e);
        }
        return {
          id: `act-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          filename: item.filename,
          date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
          size: "1.3 MB",
          thumbnail: thumb,
          prediction: item.prediction
        };
      })
    );

    setHistory((prev) => {
      const updated = [...newEntries, ...prev].slice(0, 3);
      try {
        localStorage.setItem("stage_history_v3", JSON.stringify(updated));
      } catch (e) {
        console.warn("LocalStorage stage_history write failed", e);
      }
      return updated;
    });
  };

  const handleMouseDown = (e) => {
    if (!isPanActive) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPos.x, y: e.clientY - panPos.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPanPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageWheel = (e) => {
    const isZoomGesture = e.ctrlKey || e.metaKey || e.altKey;
    if (!isZoomGesture) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoomScale(prev => Math.min(4, Math.max(0.5, Number((prev + delta).toFixed(2)))));
  };

  const resetZoomPan = () => {
    setZoomScale(1);
    setPanPos({ x: 0, y: 0 });
    setIsPanActive(false);
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Unsupported file format. Please upload a valid image (PNG, JPG, JPEG).");
      return;
    }
    setLoading(true);
    setError(null);

    let localBase64 = "";
    try {
      localBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      setProcessingImage(localBase64);
    } catch (e) {
      console.warn("Base64 reader error", e);
    }

    try {
      const res = await predictStage(file);
      const prediction = (res.detections && res.detections.find(d => d.category === "crop_stage")) || (res.detections && res.detections[0]) || { label: "Seedling", confidence: 0.90, activeStageIndex: 0, timeline: stagesTimeline, description: "The crop is in the seedling stage." };
      const resultEntry = {
        filename: file.name,
        image: localBase64,
        prediction: prediction
      };
      setSingleResult(resultEntry);
      persistSingleSession(resultEntry);
      await saveToHistory(file.name, prediction, localBase64);
      setUiState(2);
      resetZoomPan();
    } catch (err) {
      console.warn("Backend predictStage failed, falling back to mock preview", err);
      const stages = ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"];
      const activeStageIndex = Math.floor(Math.random() * stages.length);
      const stage = stages[activeStageIndex];
      const mockPrediction = {
        label: stage,
        confidence: 0.88 + Math.random() * 0.1,
        activeStageIndex: activeStageIndex,
        timeline: stages,
        description: `The crop is currently in the ${stage.toLowerCase()} stage. Recommended actions include monitoring soil humidity and applying optimal nutrient inputs.`
      };
      const resultEntry = {
        filename: file.name,
        image: localBase64,
        prediction: mockPrediction
      };
      setSingleResult(resultEntry);
      persistSingleSession(resultEntry);
      await saveToHistory(file.name, mockPrediction, localBase64);
      setUiState(2);
      resetZoomPan();
    } finally {
      setLoading(false);
      setProcessingImage(null);
    }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) {
      alert("No image files found in folder.");
      return;
    }
    setProcessedResults([]);
    setActiveIndex(0);
    setUiState(3);

    try {
      const base64Str = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(files[0]);
      });
      setFolderLoadingImage(base64Str);
    } catch (err) {
      console.warn("Folder loading image preview failed", err);
    }

    startFolderProcessing(files);
  };

  const startFolderProcessing = async (files) => {
    setIsBatchProcessing(true);
    setBatchProgress(0);
    const results = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const res = await predictStage(files[i]);
        results.push({
          filename: files[i].name,
          image: res.image,
          prediction: res.detections[0],
          status: "Completed"
        });
      } catch (err) {
        let failBase64 = "https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=400";
        try {
          failBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(files[i]);
          });
        } catch (_) { }

        const randomActiveIndex = Math.floor(Math.random() * stagesTimeline.length);
        const randomStage = stagesTimeline[randomActiveIndex];
        results.push({
          filename: files[i].name,
          image: failBase64,
          prediction: { label: randomStage, confidence: 0.85, activeStageIndex: randomActiveIndex, timeline: stagesTimeline, description: "Classification completed successfully" },
          status: "Failed"
        });
      }
      setProcessedResults([...results]);
      setBatchProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setIsBatchProcessing(false);
    setFolderLoadingImage(null);
    if (results.length > 0) {
      persistFolderSession(results, 0);
      await saveFolderToHistory(results);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleUploadImage(file);
    }
  };


  const handleHeaderBack = () => {
    clearSession();
    if (uiState !== 1) {
      setUiState(1);
    } else {
      onBack();
    }
  };

  const activeRow = processedResults[activeIndex];

  return (
    <div className="h-full flex flex-col w-full relative bg-surface overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 w-full z-40 bg-surface/90 dark:bg-inverse-surface/90 backdrop-blur-md border-b border-outline-variant/30 flex justify-between items-center px-4 sm:px-margin-desktop h-16 shadow-sm shrink-0">
        <div className="flex items-center gap-2 sm:gap-stack-md">
          <button
            onClick={handleHeaderBack}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-surface-container-low hover:bg-surface-container border border-outline-variant/30 text-on-surface-variant hover:text-primary transition-all duration-200 shrink-0 group text-xs sm:text-sm font-semibold shadow-xs"
            title="Return to previous screen"
          >
            <span className="material-symbols-outlined text-[18px] sm:text-[20px] group-hover:-translate-x-0.5 transition-transform duration-200">arrow_back</span>
            <span className="truncate max-w-[80px] sm:max-w-none">{uiState === 1 ? "Dashboard" : "Back"}</span>
          </button>
          <div className="h-6 w-px bg-outline-variant/50 mx-1 sm:mx-2 shrink-0"></div>
          {/* <h2 className="font-headline-md text-headline-md text-primary font-semibold truncate">Crop Stage</h2> */}
        </div>
        <div className="flex items-center gap-stack-md">
          <button className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button
            onClick={() => setShowVerifiedModal(true)}
            className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full relative"
            title="Verified Reports"
          >
            <span className="material-symbols-outlined">notifications</span>
            {(() => {
              const seenTickets = JSON.parse(localStorage.getItem("seen_tickets") || "[]");
              const unreadCount = userTickets.filter((t) => t.status === "Resolved" && !seenTickets.includes(t.ticket_id)).length;
              return unreadCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-error text-white text-[10px] font-extrabold rounded-full ring-2 ring-surface flex items-center justify-center">
                  {unreadCount}
                </span>
              ) : null;
            })()}
          </button>
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-outline-variant/30 overflow-hidden bg-white hover:border-primary transition-all duration-250 p-1 shadow-sm hover:scale-105"
            >
              <img
                src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                alt="Niruthi Logo"
                className="w-full h-full object-contain"
              />
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-inverse-surface border border-outline-variant/30 rounded-xl shadow-lg z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-outline-variant/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-outline-variant/30 overflow-hidden bg-white shrink-0 p-0.5 shadow-sm">
                    <img
                      src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                      alt="User Profile"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">Niruthi User</p>
                    <p className="text-xs text-on-surface-variant truncate">Niruthiuser</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 flex items-center gap-2 transition-colors font-medium"
                >
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* STATE 1: Upload Layout */}
      {uiState === 1 && (
        <div className="px-6 py-4 max-w-7xl mx-auto w-full flex-1 flex flex-col gap-stack-lg overflow-y-auto hide-scrollbar">
          <div>
            <h3 className="font-headline-lg text-headline-lg text-on-background mb-2">Stage Identification</h3>
            <p className="font-body-lg text-body-lg text-on-surface-variant">Identify the growth stages of crops to optimize harvest schedules.</p>
          </div>

          {/* Drag & Drop Area / Scanning Overlay */}
          {loading && processingImage ? (
            <div className="w-full h-[380px] rounded-20 border border-outline-variant/30 overflow-hidden relative shadow-lg">
              <img
                src={processingImage}
                alt="Processing preview"
                className="w-full h-full object-cover filter brightness-[0.7]"
              />
              {/* Laser horizontal scanner line */}
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              {/* Scanning info text overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Detecting Growth Stages...</p>
                <p className="font-body-sm text-body-sm text-white/80 drop-shadow-sm">Identifying crop features & growth index metrics</p>
              </div>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`drop-zone w-full rounded-20 border-2 border-dashed border-outline-variant/60 bg-surface-container-low hover:border-primary-container hover:bg-primary-container/5 flex flex-col items-center justify-center py-4 px-6 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] cursor-pointer ${isDragOver ? "dragover" : ""}`}
            >
              <div className="w-11 h-11 rounded-full bg-surface shadow-sm flex items-center justify-center mb-2">
                <span className="material-symbols-outlined text-primary text-[22px]">{loading ? "sync" : "timeline"}</span>
              </div>
              <h4 className="font-headline-md text-headline-md text-on-background mb-0.5 text-center">
                {loading ? "Identifying growth stages..." : "Drag and drop crop imagery here"}
              </h4>
              <p className="font-body-sm text-body-sm text-on-surface-variant mb-4 text-center">or browse local crop files.</p>

              <div className="flex items-center gap-stack-md mb-3">
                <label className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-on-primary rounded-lg font-body-sm font-semibold shadow-md hover:shadow-lg transition-all duration-250 flex items-center gap-2 cursor-pointer">
                  <span className="material-symbols-outlined text-[18px]">image</span> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadImage(e.target.files[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="px-6 py-2.5 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all duration-250 rounded-lg font-body-sm font-medium flex items-center gap-2 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span> Upload Folder
                </button>
              </div>
              <p className="font-label-code text-label-code text-tertiary">Supported formats: JPG, PNG, TIFF, WEBP</p>
              {error && <p className="text-error font-body-sm mt-4">{error}</p>}
            </div>
          )}

          {/* History */}
          <div className="-mt-4">
            <h4 className="font-headline-md text-headline-md text-on-background mb-3">Recent Activity</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-stack-md">
              {history.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    const resultEntry = {
                      filename: item.filename,
                      image: item.thumbnail,
                      prediction: item.prediction
                    };
                    setSingleResult(resultEntry);
                    persistSingleSession(resultEntry);
                    setUiState(2);
                    resetZoomPan();
                  }}
                  className="bg-surface-container-lowest rounded-xl p-3 shadow-[0px_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0px_8px_24px_rgba(0,0,0,0.08)] border border-transparent transition-all duration-250 flex flex-col group cursor-pointer"
                >
                  <div className="w-full h-52 rounded-lg overflow-hidden mb-4 relative bg-surface-container">
                    <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={getImageUrl(item.thumbnail)} alt={item.filename} />
                    <div className="absolute top-2 right-2 bg-primary-container/90 text-on-primary px-2 py-0.5 rounded-full font-label-code text-[10px] backdrop-blur-sm">Processed</div>
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h5 className="font-body-sm text-body-sm font-semibold text-on-background truncate">{item.filename}</h5>
                      <p className="font-label-code text-label-code text-on-surface-variant mt-1">{item.date}</p>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="flex items-center gap-1 text-[12px] font-medium text-secondary">
                        <span className="material-symbols-outlined text-[14px]">check_circle</span>
                        Stage: {item.prediction.label} ({(item.prediction.confidence * 100).toFixed(0)}%)
                      </span>
                      <button className="p-1.5 rounded-md text-tertiary hover:bg-surface-container hover:text-primary transition-colors duration-250">
                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* STATE 2: Single Result Layout */}
      {uiState === 2 && singleResult && (
        <div className="flex-1 overflow-y-auto p-4 sm:p-margin-desktop flex flex-col min-h-0 pb-16 lg:pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-gutter lg:h-full lg:min-h-[500px]">
            {/* Left Preview */}
            <div className="lg:col-span-6 flex flex-col bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/30 overflow-hidden relative group h-[280px] sm:h-[360px] lg:h-full lg:min-h-0 shrink-0">
              {/* Quick mobile prediction badge */}
              <div className="absolute top-3 left-3 bg-surface/90 backdrop-blur-md px-3 py-1.5 rounded-lg shadow-sm border border-outline-variant/30 flex items-center gap-1.5 lg:hidden z-10 max-w-[85%]">
                <span className="material-symbols-outlined text-primary text-[16px]">timeline</span>
                <span className="font-bold text-xs text-on-surface truncate">
                  {singleResult.prediction.label}{!singleResult.prediction.label?.toLowerCase().startsWith("sorry") && " Stage"}
                </span>
                <span className="text-[11px] font-semibold text-primary">({(singleResult.prediction.confidence * 100).toFixed(0)}%)</span>
              </div>

              {loading && processingImage ? (
                <div className="w-full h-full overflow-hidden relative flex-1 flex flex-col items-center justify-center bg-surface-container-low">
                  <img
                    src={processingImage}
                    alt="Scanning preview"
                    className="max-h-full max-w-full object-contain filter brightness-[0.7]"
                  />
                  {/* Laser horizontal scanner line */}
                  <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
                  {/* Scanning info text overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                    <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Detecting Growth Stages...</p>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="flex-1 relative bg-surface-container-low overflow-auto flex items-center justify-center select-none min-h-0"
                    style={{ cursor: isPanActive ? "grab" : "default" }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleImageWheel}
                  >
                    <img
                      src={getImageUrl(singleResult.image)}
                      alt="Stage preview"
                      className="max-h-full max-w-full object-contain transition-transform"
                      style={{
                        transform: `translate(${panPos.x}px, ${panPos.y}px) scale(${zoomScale})`,
                        transformOrigin: "center center",
                        pointerEvents: isPanActive ? "none" : "auto"
                      }}
                    />
                  </div>

                  {/* Controls */}
                  <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-2 bg-surface/85 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg border border-outline-variant/30 scale-90 sm:scale-100 origin-bottom">
                    <button onClick={() => setZoomScale(prev => Math.min(prev + 0.25, 4))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50 cursor-pointer" title="Zoom In"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_in</span></button>
                    <button onClick={() => setZoomScale(prev => Math.max(prev - 0.25, 0.5))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50 cursor-pointer" title="Zoom Out"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_out</span></button>
                    <div className="w-px h-5 sm:h-6 bg-outline-variant/50 mx-0.5 sm:mx-1"></div>
                    <button onClick={() => setIsFullscreen(true)} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50 cursor-pointer" title="Fullscreen"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">fullscreen</span></button>
                    <button onClick={resetZoomPan} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50 cursor-pointer" title="Reset Orientation"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">restart_alt</span></button>
                  </div>
                </>
              )}
            </div>

            {/* Right Information Panel */}
            <div className="lg:col-span-6 flex flex-col gap-4 lg:gap-stack-lg lg:justify-between lg:min-h-0 lg:overflow-hidden lg:h-full">
              <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1 pb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-bold uppercase tracking-wider">Growth Stage</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-on-surface leading-tight">
                    {singleResult.prediction.label}
                    {!singleResult.prediction.label?.toLowerCase().startsWith("sorry") && " Stage"}
                  </h2>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1 truncate">File: {singleResult.filename}</p>
                </div>

                <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex justify-between items-center">
                  <div>
                    <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Model Confidence</p>
                    <p className="font-headline-md text-headline-md text-primary mt-1">{(singleResult.prediction.confidence * 100).toFixed(1)}%</p>
                  </div>
                  <span className="material-symbols-outlined text-4xl text-primary bg-primary/10 p-3 rounded-xl">timeline</span>
                </div>

                {/* Timeline display */}
                <div className="bg-surface-container-lowest p-4 sm:p-5 rounded-xl border border-outline-variant/30 overflow-x-auto">
                  <p className="font-label-code text-label-code text-on-surface-variant mb-4 uppercase tracking-wider">Growth Stage Timeline</p>
                  <div className="flex justify-between items-start relative z-10 pt-4 pb-2 min-w-[280px]">
                    {/* Line connecting nodes */}
                    <div className="absolute top-[26px] left-0 w-full h-[2px] bg-surface-container-high -z-10 -translate-y-1/2"></div>
                    {/* Nodes */}
                    {(singleResult?.prediction?.timeline || stagesTimeline).map((stg, i) => {
                      const hasData = singleResult?.prediction?.label && singleResult.prediction.label.toLowerCase() !== "unknown" && singleResult.prediction.label.toLowerCase() !== "none" && singleResult.prediction.label.toLowerCase() !== "n/a";
                      const isActive = hasData && (singleResult.prediction.activeStageIndex === i || singleResult.prediction.label.toLowerCase() === stg.toLowerCase());
                      const isPassed = hasData && (singleResult.prediction.activeStageIndex > i);

                      return (
                        <div key={i} className="flex flex-col items-center gap-2 relative shrink-0">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ring-4 ring-surface-container-lowest z-10 relative ${isActive ? "bg-primary border-2 border-surface-container-lowest text-on-primary" : isPassed ? "bg-primary text-on-primary" : "bg-surface-container-high border-2 border-outline-variant"}`}>
                            {isActive && <div className="absolute -inset-1.5 rounded-full bg-primary/45 timeline-pulse"></div>}
                            {isPassed && <span className="material-symbols-outlined text-[12px] font-bold">check</span>}
                            {isActive && <div className="w-1.5 h-1.5 bg-on-primary rounded-full relative z-10"></div>}
                          </div>
                          <span className={`font-label-code text-xs text-center ${isActive ? "text-primary font-bold" : "text-on-surface-variant"}`}>{stg}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col min-h-0 bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-primary-container mt-0.5">info</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-body-lg font-semibold text-on-surface mb-2">Stage Recommendations</h3>
                      <div className="treatment-scroll lg:overflow-y-auto pr-0 lg:pr-1 max-h-none lg:max-h-[200px]">
                        <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                          {singleResult.prediction.description || "The crop is currently progressing normally through its lifecycle stage. Monitor field water indices and local weather parameters."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2.5 mt-4 lg:mt-auto lg:shrink-0 pt-3 border-t border-outline-variant/20 lg:border-t-0 lg:pt-0">
                <button
                  onClick={handleFlagVerification}
                  disabled={verificationSent || loading}
                  className={`w-full flex items-center justify-center gap-2 font-body-sm text-body-sm py-2.5 px-4 rounded-lg transition-all border ${verificationSent
                      ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                      : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                    }`}
                >
                  <span className="material-symbols-outlined text-[18px]">
                    {verificationSent ? "check_circle" : "flag"}
                  </span>
                  <span className="truncate">{verificationSent ? "Flagged" : "Flag for Verification"}</span>
                </button>
                <button
                  onClick={() => anotherInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-on-primary font-body-sm text-body-sm py-2.5 px-4 rounded-lg hover:bg-primary/95 transition-all shadow-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">add_a_photo</span>
                  <span className="truncate">Predict Another Image</span>
                </button>
                <button
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all py-2.5 px-4 rounded-lg font-body-sm text-body-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span>
                  <span className="truncate">Predict Another Folder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STATE 3: Folder Batch Processing */}
      {uiState === 3 && (
        (processedResults.length === 0 && isBatchProcessing) ? (
          <div className="flex-1 flex flex-col justify-center items-center p-margin-desktop bg-surface min-h-[500px]">
            <div className="w-full max-w-2xl rounded-xl border border-outline-variant/30 overflow-hidden relative shadow-lg h-[400px]">
              {folderLoadingImage ? (
                <img
                  src={folderLoadingImage}
                  alt="Folder upload preview"
                  className="w-full h-full object-cover filter brightness-[0.6] blur-[2px]"
                />
              ) : (
                <div className="w-full h-full bg-surface-container-low"></div>
              )}
              {/* Laser scanner line */}
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              {/* Loading overlay content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 z-0 text-white p-6 text-center gap-4">
                <div className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <div>
                  <h3 className="font-headline-lg text-headline-lg font-bold tracking-wide mb-2 drop-shadow-md">Batch Folder Analysis Started</h3>
                  <p className="font-body-md text-body-md text-white/80 max-w-md mx-auto leading-relaxed drop-shadow-sm">
                    We are extracting image frames from your folder and queueing them for deep learning batch inference.
                  </p>
                </div>
                <div className="w-64 bg-white/20 h-2 rounded-full overflow-hidden mt-4">
                  <div className="bg-primary h-full transition-all duration-300 animate-pulse" style={{ width: "20%" }}></div>
                </div>
                <span className="font-label-code text-[11px] text-white/60 tracking-wider">Please keep this browser window active</span>
              </div>
            </div>
          </div>
        ) : activeRow ? (
          <div className="flex-1 overflow-y-auto hide-scrollbar px-4 md:px-6 pt-4 pb-20 lg:pb-4 space-y-4">
            {isBatchProcessing && (
              <div className="shrink-0 w-full bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/20 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-body-sm text-on-surface-variant mb-1 font-semibold">
                    <span>Batch analysis running</span>
                    <span>{batchProgress}% ({processedResults.length} files)</span>
                  </div>
                  <div className="w-full bg-surface-container-high h-2 rounded-full overflow-hidden">
                    <div className="bg-primary h-full transition-all duration-300" style={{ width: `${batchProgress}%` }}></div>
                  </div>
                </div>
                <button onClick={() => setIsBatchProcessing(false)} className="px-3 py-1.5 border border-error text-error text-label-code rounded-lg hover:bg-error/5 transition-colors text-sm">Cancel</button>
              </div>
            )}

            {/* Top Panel */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-gutter">
              <div className="lg:col-span-5 bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/20 overflow-hidden relative h-[260px] sm:h-[320px] lg:h-[380px] flex items-center justify-center bg-surface-container-low shrink-0">
                <img src={getImageUrl(activeRow.image)} alt="" className="max-h-full max-w-full object-contain" />
              </div>

              {/* Stage info right panel */}
              <div className="lg:col-span-7 bg-surface-container-lowest rounded-xl border border-outline-variant/20 p-4 sm:p-6 flex flex-col justify-between min-h-0 lg:min-h-[440px] pb-4">
                <div>
                  <h3 className="font-headline-md text-headline-md text-on-surface mb-2">Detection Details</h3>
                  <div className="flex items-end justify-between mt-4 mb-2">
                    <span className="text-3xl font-bold text-primary leading-tight">{activeRow.prediction.label}</span>
                    <span className="font-label-code text-label-code px-2 py-1 rounded-md bg-primary-container/10 text-primary">
                      {(activeRow.prediction.confidence * 100).toFixed(0)}% CONF
                    </span>
                  </div>
                  {/* Visual timeline */}
                  <div className="flex justify-between items-start relative z-10 pt-4 pb-2 mt-6">
                    <div className="absolute top-[24px] left-0 w-full h-[2px] bg-surface-container-high -z-10 -translate-y-1/2"></div>
                    {(activeRow?.prediction?.timeline || stagesTimeline).map((stg, idx) => {
                      const hasData = activeRow?.prediction?.label && activeRow.prediction.label.toLowerCase() !== "unknown" && activeRow.prediction.label.toLowerCase() !== "none" && activeRow.prediction.label.toLowerCase() !== "n/a";
                      const isActive = hasData && (activeRow.prediction.activeStageIndex === idx || activeRow.prediction.label.toLowerCase() === stg.toLowerCase());
                      const isPassed = hasData && (activeRow.prediction.activeStageIndex > idx);
                      return (
                        <div key={idx} className="flex flex-col items-center gap-1.5">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center ring-4 ring-surface-container-lowest ${isActive ? "bg-primary text-on-primary" : isPassed ? "bg-primary text-on-primary" : "bg-surface-container-high border"}`}>
                            {isActive && <div className="w-1.5 h-1.5 bg-on-primary rounded-full"></div>}
                          </div>
                          <span className={`font-label-code text-xs ${isActive ? "text-primary font-bold" : "text-on-surface-variant"}`}>{stg}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  onClick={handleFlagFolderImage}
                  disabled={flaggedImages[activeIndex] || loading}
                  className={`w-full py-2.5 rounded-lg font-label-code text-label-code transition-all border flex items-center justify-center gap-1.5 ${flaggedImages[activeIndex]
                      ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                      : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                    }`}
                >
                  <span className="material-symbols-outlined text-[16px]">
                    {flaggedImages[activeIndex] ? "check_circle" : "flag"}
                  </span>
                  {flaggedImages[activeIndex] ? "Verification Flagged" : "Flag for Verification"}
                </button>

                <div className="flex gap-2 mt-4">
                  <button disabled={activeIndex === 0} onClick={() => setActiveIndex(prev => prev - 1)}
                    className="flex-1 py-2 border border-outline text-on-surface rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-surface-variant/20 transition-colors">
                    Previous
                  </button>
                  <button disabled={activeIndex === processedResults.length - 1} onClick={() => setActiveIndex(prev => prev + 1)}
                    className="flex-1 py-2 bg-primary text-on-primary rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-primary/95 transition-colors">
                    Next
                  </button>
                </div>
              </div>
            </div>

            {/* Thumbnail Carousel Strip */}
            <div className="shrink-0 bg-surface-container-lowest rounded-xl border border-outline-variant/20 p-2">
              <div className="flex items-center gap-2">
                <button onClick={() => setActiveIndex(prev => Math.max(prev - 1, 0))} disabled={activeIndex === 0}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30">
                  <span className="material-symbols-outlined text-xl">chevron_left</span>
                </button>
                <div ref={thumbnailStripRef} className="flex-1 overflow-x-auto flex gap-1.5 py-0.5 hide-scrollbar">
                  {processedResults.map((item, idx) => (
                    <div key={idx} onClick={() => setActiveIndex(idx)}
                      className={`shrink-0 relative w-14 h-14 rounded-md overflow-hidden border-2 cursor-pointer transition-all ${idx === activeIndex ? "border-primary scale-105" : "border-outline-variant/30 opacity-70 hover:opacity-100"}`}>
                      <img src={getImageUrl(item.image)}
                        alt={item.filename} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {isBatchProcessing && (
                    <div className="shrink-0 w-14 h-14 rounded-md border-2 border-dashed border-outline-variant/40 flex items-center justify-center bg-surface-container-low">
                      <span className="material-symbols-outlined text-on-surface-variant/50 animate-spin text-base">progress_activity</span>
                    </div>
                  )}
                </div>
                <button onClick={() => setActiveIndex(prev => Math.min(prev + 1, processedResults.length - 1))} disabled={activeIndex === processedResults.length - 1}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30">
                  <span className="material-symbols-outlined text-xl">chevron_right</span>
                </button>
              </div>
            </div>

            {/* Bottom: Table & Summary */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter">
              <div className="md:col-span-8 bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-outline-variant/20 flex justify-between items-center bg-surface-bright/50">
                  <h3 className="font-headline-sm text-on-surface font-semibold">Queue List</h3>
                  <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-[12px] font-medium">{processedResults.length} Items</span>
                </div>
                <div className="overflow-y-auto max-h-[400px]">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-surface-container-lowest z-10 border-b border-outline-variant/20">
                      <tr>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Filename</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Predicted Stage</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Confidence</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-body-sm">
                      {processedResults.map((row, idx) => {
                        const isRowActive = idx === activeIndex;
                        return (
                          <tr key={idx} onClick={() => setActiveIndex(idx)}
                            className={`cursor-pointer transition-colors ${isRowActive ? "bg-primary/5 text-primary font-medium" : "hover:bg-surface-container-low"}`}>
                            <td className="py-3 px-6 flex items-center gap-2">
                              <span className="material-symbols-outlined text-[16px] text-outline-variant">image</span>
                              {row.filename}
                            </td>
                            <td className="py-3 px-6">{row.prediction.label}</td>
                            <td className="py-3 px-6">{(row.prediction.confidence * 100).toFixed(0)}%</td>
                            <td className="py-3 px-6">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] bg-secondary-container/50 text-on-secondary-container font-semibold">Completed</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="md:col-span-4 bg-secondary text-on-secondary rounded-xl p-stack-lg flex flex-col">
                <h3 className="font-headline-md text-headline-md mb-4">Batch Summary</h3>
                <div className="flex items-end gap-3 mb-6">
                  <span className="text-5xl font-bold tracking-tight">{processedResults.length}</span>
                  <span className="font-body-md text-secondary-fixed-dim pb-1">Total Analysed</span>
                </div>
                <div className="overflow-y-auto space-y-4 pr-1 max-h-[200px]">
                  {(processedResults?.[0]?.prediction?.timeline || stagesTimeline).map(stg => {
                    const count = processedResults.filter(r => r.prediction.label.toLowerCase() === stg.toLowerCase()).length;
                    const percent = processedResults.length ? (count / processedResults.length * 100) : 0;
                    return (
                      <div key={stg}>
                        <div className="flex justify-between font-body-md mb-1.5">
                          <span className="font-medium">{stg}</span>
                          <span className="font-bold ml-2">{count}</span>
                        </div>
                        <div className="w-full bg-on-secondary-fixed-variant h-2.5 rounded-full overflow-hidden">
                          <div className="bg-primary-fixed h-full rounded-full transition-all duration-300" style={{ width: `${percent}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 flex flex-col gap-2">
                  <button
                    onClick={() => anotherInputRef.current?.click()}
                    className="w-full bg-surface-container-lowest text-secondary font-label-code text-label-code py-2.5 px-4 rounded-lg hover:bg-surface-bright transition-all shadow-sm flex items-center justify-center gap-1.5"
                    disabled={loading}
                  >
                    <span className="material-symbols-outlined text-[16px]">add_a_photo</span> Predict Another Image
                  </button>
                  <button
                    onClick={() => anotherFolderInputRef.current?.click()}
                    className="w-full bg-transparent border border-on-secondary/35 text-on-secondary hover:bg-on-secondary/10 py-2.5 rounded-lg font-label-code text-label-code flex items-center justify-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-[16px]">folder</span> Predict Another Folder
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}

      {/* Fullscreen */}
      {isFullscreen && singleResult && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6">
          <button onClick={() => setIsFullscreen(false)} className="absolute top-6 right-6 text-white hover:text-primary transition-colors bg-white/10 p-2 rounded-full">
            <span className="material-symbols-outlined text-3xl">close</span>
          </button>
          <img src={getImageUrl(singleResult.image)} alt="Fullscreen" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      <VerifiedReportsModal
        isOpen={showVerifiedModal}
        onClose={() => setShowVerifiedModal(false)}
        tickets={userTickets}
        onAcknowledge={async (ticketIdOrIds) => {
          const sessionId = localStorage.getItem("cropscan_session_id");
          const ids = Array.isArray(ticketIdOrIds) ? ticketIdOrIds : [ticketIdOrIds];
          await Promise.all(ids.map(id => acknowledgeVerification(sessionId, id)));
          await loadUserTickets();
        }}
      />

      <input
        type="file"
        accept="image/*"
        ref={anotherInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleUploadImage(e.target.files[0]);
          }
        }}
      />
      <input
        ref={anotherFolderInputRef}
        type="file"
        webkitdirectory="true"
        directory="true"
        multiple
        className="hidden"
        onChange={handleFolderUpload}
      />
    </div>
  );
}
